import React, { useState, useEffect, useRef } from 'react';
import { getManagers, getHotelProductTypes, getCurrencyCodes } from '../services/apiService';
import { getHotelBasicInfo, getHotelDetailInfo, saveHotelDetailInfo } from '../services/productService';
import KeywordsInput from './shared/KeywordsInput';
import { FieldRow, IconButton, InputField, DropdownField } from './shared/FormComponents';
import CitySelector from './shared/CitySelector';
 

const HotelSettingsModal = ({ product, onClose, onSave, loading }) => {
  const [formData, setFormData] = useState(product);
  const [activeTab, setActiveTab] = useState('basic'); // 'basic' | 'detail'
  const [hotelBasicLoading, setHotelBasicLoading] = useState(false);
  const [detailState, setDetailState] = useState({ loading: false, error: '', detail: null });
  const [dropdowns, setDropdowns] = useState({});
  const [managerOptions, setManagerOptions] = useState([]);
  const [productTypeOptions, setProductTypeOptions] = useState([]);
  const [currencyOptions, setCurrencyOptions] = useState([]);
  const [currencyLoading, setCurrencyLoading] = useState(false);
  
  // Animation state
  const [mountedVisible, setMountedVisible] = useState(false);
  const [animatingOut, setAnimatingOut] = useState(false);
  const [hasDOM, setHasDOM] = useState(false);
  const backdropMouseDownRef = useRef(false);

  useEffect(() => {
    const id = requestAnimationFrame(() => setMountedVisible(true));
    const domReady = typeof document !== 'undefined' && !!document.body;
    setHasDOM(domReady);
    return () => cancelAnimationFrame(id);
  }, []);

  useEffect(() => {
    setFormData(product);
  }, [product]);

  // Load 호텔 기초설정 값
  useEffect(() => {
    let mounted = true;
    const loadHotelBasic = async () => {
      try {
        if (!product?.상품번호) return;
        setHotelBasicLoading(true);
        const row = await getHotelBasicInfo(product.상품번호).catch(() => null);
        if (!mounted) return;
        if (row) {
          setFormData(prev => ({
            ...prev,
            상품명: row.내부상품명 ?? prev.상품명,
            상품코드: row.상품코드 ?? prev.상품코드,
            도시: row.카테고리 ?? row.도시 ?? prev.도시,
            상품타입: row.상품타입 ?? prev.상품타입,
            상품담당: row.상품담당 ?? prev.상품담당,
            예약담당: row.예약담당 ?? prev.예약담당,
            체크인시간: row.체크인시간 ?? prev.체크인시간,
            체크아웃시간: row.체크아웃시간 ?? prev.체크아웃시간,
            원가통화: row.원가통화 ?? prev.원가통화,
            공급거래처: row.공급거래처 ?? prev.공급거래처,
            장소명: row.장소명 ?? prev.장소명,
            키워드: row.키워드 ?? prev.키워드,
            상품뱃지: row.상품뱃지 ?? prev.상품뱃지,
            
          }));
        }
      } finally {
        setHotelBasicLoading(false);
      }
    };
    loadHotelBasic();
    return () => { mounted = false; };
  }, [product]);

  // Load currency list
  useEffect(() => {
    let mounted = true;
    const ensureCurrencies = async () => {
      try {
        setCurrencyLoading(true);
        const list = await getCurrencyCodes();
        if (!mounted) return;
        setCurrencyOptions(Array.isArray(list) ? list : []);
      } catch (e) {
        if (!mounted) return;
        setCurrencyOptions([]);
      } finally {
        setCurrencyLoading(false);
      }
    };
    ensureCurrencies();
    return () => { mounted = false; };
  }, []);

  // Load 호텔 상세설정 when switching to detail tab
  useEffect(() => {
    let mounted = true;
    const loadDetail = async () => {
      if (!product?.상품번호) return;
      try {
        setDetailState(prev => ({ ...prev, loading: true, error: '' }));
        const res = await getHotelDetailInfo(product.상품번호);
        if (!mounted) return;
        const rawDetail = res.detail || {};
        setDetailState({ loading: false, error: '', detail: rawDetail });
      } catch (e) {
        if (!mounted) return;
        setDetailState(prev => ({ ...prev, loading: false, error: '상세 정보를 불러오는데 실패했습니다.' }));
      }
    };
    if (activeTab === 'detail' && detailState.detail == null) {
      loadDetail();
    }
    return () => { mounted = false; };
  }, [activeTab, product]);

  // Load managers
  useEffect(() => {
    let mounted = true;
    const fetchManagers = async () => {
      try {
        const list = await getManagers();
        if (!mounted) return;
        setManagerOptions(Array.isArray(list) ? list.map((m) => m.코드명) : []);
      } catch (e) {
        if (!mounted) return;
        setManagerOptions([]);
      }
    };
    fetchManagers();
    return () => { mounted = false; };
  }, []);

  // Load product types
  useEffect(() => {
    let mounted = true;
    const fetchProductTypes = async () => {
      try {
        const list = await getHotelProductTypes();
        if (!mounted) return;
        setProductTypeOptions(Array.isArray(list) ? list.map((t) => t.코드명) : []);
      } catch (e) {
        if (!mounted) return;
        setProductTypeOptions([]);
      }
    };
    fetchProductTypes();
    return () => { mounted = false; };
  }, []);

  const dropdownOptions = {
    '도시': [],
    '상품타입': productTypeOptions,
    '상품담당': managerOptions,
    '예약담당': managerOptions,
  };

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({ ...prev, [name]: type === 'checkbox' ? checked : value }));
  };

  const handleDropdownChange = (name, value) => {
    setFormData(prev => ({ ...prev, [name]: value }));
    setDropdowns(prev => ({ ...prev, [name]: false }));
  };

  const handleDetailChange = (field, value) => {
    setDetailState(prev => ({
      ...prev,
      detail: { ...prev.detail, [field]: value }
    }));
  };
 

  const toggleDropdown = (name) => {
    setDropdowns(prev => ({
      ...prev,
      [name]: !prev[name]
    }));
  };

  const requestClose = () => {
    setAnimatingOut(true);
    setMountedVisible(false);
    window.setTimeout(() => {
      onClose && onClose();
    }, 220);
  };

  const handleSave = async () => {
    if (activeTab === 'basic') {
      onSave && onSave(formData);
    } else if (activeTab === 'detail') {
      try {
        if (!product?.상품번호) {
          alert('상품번호가 없습니다.');
          return;
        }
        await saveHotelDetailInfo({
          productNo: product.상품번호,
          detailInfo: detailState.detail
        });
        alert('상세설정이 저장되었습니다.');
        requestClose();
      } catch (error) {
        console.error('상세설정 저장 실패:', error);
        alert('상세설정 저장에 실패했습니다.');
      }
    }
  };

  if (loading) {
    if (!hasDOM) return null;
    return (
      <div className="fixed inset-0 bg-black bg-opacity-50 z-[200]">
        <div 
          className="fixed top-0 right-0 h-full w-1/3 bg-white shadow-lg z-[210] p-6 flex justify-center items-center"
          onClick={(e) => e.stopPropagation()}
        >
          <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-blue-500"></div>
        </div>
      </div>
    );
  }

  if (!product || !hasDOM) return null;

  return (
    <div
      className={`fixed inset-0 bg-black bg-opacity-40 backdrop-blur-sm z-[200] transition-opacity duration-200 ${animatingOut ? 'ease-in opacity-0' : 'ease-out'} ${mountedVisible && !animatingOut ? 'opacity-100' : 'opacity-0'}`}
      onMouseDown={(e) => {
        backdropMouseDownRef.current = (e.target === e.currentTarget);
      }}
      onMouseUp={(e) => {
        if (backdropMouseDownRef.current && e.target === e.currentTarget) {
          requestClose();
        }
        backdropMouseDownRef.current = false;
      }}
    >
      <div 
        className={`fixed top-0 right-0 h-full w-full sm:w-[520px] md:w-[620px] lg:w-[720px] bg-white shadow-2xl border-l rounded-l-2xl z-[210] p-6 overflow-y-auto transform transition-transform duration-200 ${animatingOut ? 'ease-in translate-x-full' : 'ease-out'} ${mountedVisible && !animatingOut ? 'translate-x-0' : 'translate-x-full'}`}
        onMouseDown={(e) => e.stopPropagation()}
        onMouseUp={(e) => e.stopPropagation()}
      >
        {/* Header & Tabs */}
        <div className="sticky top-0 z-10 bg-white mb-4 pb-2">
          <div className="mb-3 flex items-center justify-between">
            <div className="flex items-center gap-2">
              <div>
                <h2 className="text-lg font-semibold text-gray-900">호텔 설정</h2>
              </div>
            </div>
            <button
              type="button"
              aria-label="닫기"
              onClick={requestClose}
              className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor" className="h-5 w-5">
                <path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd" />
              </svg>
            </button>
          </div>
          <div className="inline-flex w-full rounded-md border border-gray-200 bg-gray-50 overflow-hidden text-[11px]" role="tablist" aria-label="호텔 설정 탭">
            <button
              type="button"
              role="tab"
              aria-selected={activeTab === 'basic'}
              onClick={() => setActiveTab('basic')}
              className={`flex-1 px-3 py-2.5 border-r border-gray-200 text-center transition-colors ${
                activeTab === 'basic' 
                  ? 'bg-gray-700 text-[13px] text-white font-semibold' 
                  : 'bg-gray-50 text-[13px] text-gray-800 hover:bg-gray-100'
              }`}
            >
              기초 설정
              <span className="block mt-0.5 text-[10px] font-normal opacity-80">호텔 기본 정보</span>
            </button>
            <button
              type="button"
              role="tab"
              aria-selected={activeTab === 'detail'}
              onClick={() => setActiveTab('detail')}
              className={`flex-1 px-3 py-2.5 text-center transition-colors ${
                activeTab === 'detail' 
                  ? 'bg-gray-700 text-[13px] text-white font-semibold' 
                  : 'bg-gray-50 text-[13px] text-gray-800 hover:bg-gray-100'
              }`}
            >
              상세 설정
              <span className="block mt-0.5 text-[10px] font-normal opacity-80">예약/결제 옵션</span>
            </button>
          </div>
        </div>

        {activeTab === 'basic' && (
        <div className="space-y-4">
          {/* 기본 정보 섹션 */}
          <div>
            <div className="flex items-center gap-2 mb-3 pb-2 border-b border-gray-100">
              <div className="w-1 h-5 bg-blue-500 rounded-full"></div>
              <div className="text-sm font-semibold text-gray-800">기본 정보</div>
            </div>
            <div className="pl-3 space-y-2 text-sm">
              <FieldRow 
                label="상품명"
                rightAction={<IconButton ariaLabel="상품명 편집" onClick={() => document.getElementById('상품명-input')?.focus()} />}
              >
                <InputField id="상품명-input" name="상품명" value={formData.상품명 || ''} onChange={handleChange} placeholder="상품명을 입력하세요" />
              </FieldRow>

              <FieldRow 
                label="상품코드"
                rightAction={<IconButton ariaLabel="상품코드 편집" onClick={() => document.getElementById('상품코드-input')?.focus()} />}
              >
                <InputField id="상품코드-input" name="상품코드" value={formData.상품코드 || ''} onChange={handleChange} placeholder="상품코드를 입력하세요" />
              </FieldRow>

              <FieldRow label="공급거래처 / 장소명">
                <div className="px-3 py-2 grid grid-cols-2 gap-4">
                  <div className="text-sm text-gray-700 truncate">{formData.공급거래처 || '-'}</div>
                  <div className="text-sm text-gray-700 truncate">{formData.장소명 || '-'}</div>
                </div>
              </FieldRow>

              <FieldRow label="카테고리">
                <CitySelector
                  value={formData.도시}
                  isOpen={!!dropdowns['도시']}
                  onToggle={() => toggleDropdown('도시')}
                  onChange={(value) => handleDropdownChange('도시', value)}
                  placeholder="카테고리명 또는 초성(ㄷㅅ 등)으로 검색"
                />
              </FieldRow>

              <FieldRow label="상품타입">
                <DropdownField 
                  name="상품타입" 
                  value={formData.상품타입} 
                  options={dropdownOptions['상품타입']} 
                  isOpen={dropdowns['상품타입']} 
                  onToggle={() => toggleDropdown('상품타입')} 
                  onChange={(value) => handleDropdownChange('상품타입', value)} 
                />
              </FieldRow>

              <FieldRow label="체크인/체크아웃">
                <div className="px-3 py-2 flex items-center gap-4">
                  {hotelBasicLoading ? (
                    <span className="text-gray-500">불러오는 중...</span>
                  ) : (
                    <>
                      <label className="flex items-center gap-2">
                        <span className="text-sm text-gray-600">체크인</span>
                        <input
                          type="time"
                          name="체크인시간"
                          value={formData.체크인시간 || ''}
                          onChange={handleChange}
                          className="block w-36 px-3 py-2 border rounded"
                        />
                      </label>
                      <label className="flex items-center gap-2">
                        <span className="text-sm text-gray-600">체크아웃</span>
                        <input
                          type="time"
                          name="체크아웃시간"
                          value={formData.체크아웃시간 || ''}
                          onChange={handleChange}
                          className="block w-36 px-3 py-2 border rounded"
                        />
                      </label>
                    </>
                  )}
                </div>
              </FieldRow>

              <FieldRow label="원가통화">
                <div className="space-y-1">
                  <div className="px-3 py-2 text-gray-700 rounded cursor-not-allowed">
                    {formData.원가통화 || '선택하세요'}
                  </div>
                </div>
              </FieldRow>
            </div>
          </div>

          {/* 담당자 정보 섹션 */}
          <div className="mt-6">
            <div className="flex items-center gap-2 mb-3 pb-2 border-b border-gray-100">
              <div className="w-1 h-5 bg-emerald-500 rounded-full"></div>
              <div className="text-sm font-semibold text-gray-800">담당자 정보</div>
            </div>
            <div className="pl-3 space-y-2 text-sm">
              <FieldRow label="상품담당">
                <DropdownField 
                  name="상품담당" 
                  value={formData.상품담당} 
                  options={dropdownOptions['상품담당']} 
                  isOpen={dropdowns['상품담당']} 
                  onToggle={() => toggleDropdown('상품담당')} 
                  onChange={(value) => handleDropdownChange('상품담당', value)} 
                />
              </FieldRow>
              <FieldRow label="예약담당">
                <DropdownField 
                  name="예약담당" 
                  value={formData.예약담당} 
                  options={dropdownOptions['예약담당']} 
                  isOpen={dropdowns['예약담당']} 
                  onToggle={() => toggleDropdown('예약담당')} 
                  onChange={(value) => handleDropdownChange('예약담당', value)} 
                />
              </FieldRow>
            </div>
          </div>

          {/* 추가 정보 섹션 */}
          <div className="mt-6">
            <div className="flex items-center gap-2 mb-3 pb-2 border-b border-gray-100">
              <div className="w-1 h-5 bg-indigo-500 rounded-full"></div>
              <div className="text-sm font-semibold text-gray-800">추가 정보</div>
            </div>
            <div className="pl-3 space-y-2 text-sm">
          
              <FieldRow 
                label="키워드"
                rightAction={<IconButton ariaLabel="키워드 편집" onClick={() => document.getElementById('키워드-input')?.focus()} />}
              >
                <KeywordsInput 
                  value={formData.키워드 || ''}
                  onChange={(val) => setFormData(prev => ({ ...prev, 키워드: val }))}
                  placeholder="키워드 입력 후 Enter"
                />
              </FieldRow>
              <FieldRow label="상품뱃지">
                <InputField
                  id="상품뱃지-input"
                  name="상품뱃지"
                  value={formData.상품뱃지 || ''}
                  onChange={handleChange}
                  placeholder="상품 목록에 표시할 뱃지 텍스트"
                />
              </FieldRow>
            </div>
          </div>
        </div>
        )}

        {activeTab === 'detail' && (
        <div className="space-y-4">
          {detailState.loading ? (
            <div className="flex justify-center items-center py-12">
              <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-blue-500"></div>
            </div>
          ) : detailState.error ? (
            <div className="text-red-600 text-center py-12">{detailState.error}</div>
          ) : (
            <>
              {/* 연령 설정 */}
              <div>
                <div className="flex items-center gap-2 mb-3 pb-2 border-b border-gray-100">
                  <div className="w-1 h-5 bg-blue-500 rounded-full"></div>
                  <div className="text-sm font-semibold text-gray-800">연령 설정</div>
                </div>
                <div className="pl-3 space-y-2 text-sm">
                  <FieldRow label="유아 연령">
                    <div className="px-3 py-2 flex items-center gap-2">
                      <input
                        type="number"
                        min="0"
                        value={0}
                        disabled
                        className="w-20 px-3 py-2 border rounded bg-gray-100 text-gray-500 cursor-not-allowed"
                      />
                      <span className="text-gray-600">~</span>
                      <input
                        type="number"
                        min="0"
                        value={detailState.detail?.연령유아끝 ?? 3}
                        onChange={(e) => {
                          const infantEnd = Number(e.target.value);
                          // 유아 끝 나이 변경 시 소아 시작 나이도 동기화
                          setDetailState(prev => ({
                            ...prev,
                            detail: {
                              ...prev.detail,
                              연령유아시작: 0,
                              연령유아끝: infantEnd,
                              연령소아시작: infantEnd
                            }
                          }));
                        }}
                        className="w-20 px-3 py-2 border rounded"
                      />
                      <span className="text-sm text-gray-500">세</span>
                    </div>
                  </FieldRow>
                  <FieldRow label="소아 연령">
                    <div className="px-3 py-2 flex items-center gap-2">
                      <input
                        type="number"
                        min="0"
                        value={detailState.detail?.연령소아시작 ?? 3}
                        disabled
                        className="w-20 px-3 py-2 border rounded bg-gray-100 text-gray-500 cursor-not-allowed"
                      />
                      <span className="text-gray-600">~</span>
                      <input
                        type="number"
                        min="0"
                        value={detailState.detail?.연령소아끝 ?? 13}
                        onChange={(e) => {
                          const childEnd = Number(e.target.value);
                          // 소아 끝 나이 변경 시 성인 시작 나이도 동기화
                          setDetailState(prev => ({
                            ...prev,
                            detail: {
                              ...prev.detail,
                              연령소아끝: childEnd,
                              연령성인시작: childEnd
                            }
                          }));
                        }}
                        className="w-20 px-3 py-2 border rounded"
                      />
                      <span className="text-sm text-gray-500">세</span>
                    </div>
                  </FieldRow>
                  <FieldRow label="성인 연령">
                    <div className="px-3 py-2 flex items-center gap-2">
                      <input
                        type="number"
                        min="0"
                        value={detailState.detail?.연령성인시작 ?? 13}
                        disabled
                        className="w-20 px-3 py-2 border rounded bg-gray-100 text-gray-500 cursor-not-allowed"
                      />
                      <span className="text-sm text-gray-500">세 이상</span>
                    </div>
                  </FieldRow>
                </div>
              </div>

              {/* 예약 설정 */}
              <div className="mt-6">
                <div className="flex items-center gap-2 mb-3 pb-2 border-b border-gray-100">
                  <div className="w-1 h-5 bg-emerald-500 rounded-full"></div>
                  <div className="text-sm font-semibold text-gray-800">예약 설정</div>
                </div>
                <div className="pl-3 space-y-2 text-sm">
                  <FieldRow label="대기예약 허용">
                    <div className="px-3 py-2">
                      <label className="flex items-center gap-2">
                        <input
                          type="checkbox"
                          checked={!!detailState.detail?.대기예약허용여부}
                          onChange={(e) => handleDetailChange('대기예약허용여부', e.target.checked)}
                          className="w-4 h-4"
                        />
                        <span className="text-sm text-gray-700">대기예약 허용</span>
                      </label>
                    </div>
                  </FieldRow>
                  <FieldRow label="예약확정방식">
                    <div className="px-3 py-2">
                      <select
                        value={detailState.detail?.예약확정방식 ?? '010001'}
                        onChange={(e) => handleDetailChange('예약확정방식', e.target.value)}
                        className="w-full px-3 py-2 border rounded"
                      >
                        <option value="010001">자동확정</option>
                        <option value="010002">관리자 확인 후 확정</option>
                      </select>
                    </div>
                  </FieldRow>
                  <FieldRow label="예약마감">
                    <div className="px-3 py-2 flex items-center gap-2">
                      <input
                        type="number"
                        min="0"
                        value={detailState.detail?.예약마감 ?? 0}
                        onChange={(e) => handleDetailChange('예약마감', Number(e.target.value))}
                        className="w-24 px-3 py-2 border rounded"
                      />
                      <select
                        value={detailState.detail?.예약마감시간타입 ?? '012001'}
                        onChange={(e) => handleDetailChange('예약마감시간타입', e.target.value)}
                        className="px-3 py-2 border rounded"
                      >
                        <option value="012001">일</option>
                        <option value="012002">시간</option>
                      </select>
                      <span className="text-sm text-gray-500">전 마감</span>
                    </div>
                  </FieldRow>
                </div>
              </div>

              {/* 신청금 설정 */}
              <div className="mt-6">
                <div className="flex items-center gap-2 mb-3 pb-2 border-b border-gray-100">
                  <div className="w-1 h-5 bg-indigo-500 rounded-full"></div>
                  <div className="text-sm font-semibold text-gray-800">신청금 설정</div>
                </div>
                <div className="pl-3 space-y-2 text-sm">
                  <FieldRow label="신청금 구분">
                    <div className="px-3 py-2">
                      <select
                        value={detailState.detail?.신청금구분 ?? '004001'}
                        onChange={(e) => {
                          const newType = e.target.value;
                          handleDetailChange('신청금구분', newType);
                          // 전체금액이면 신청금을 100으로 고정
                          if (newType === '004001') {
                            handleDetailChange('신청금', 100);
                          }
                        }}
                        className="w-full px-3 py-2 border rounded"
                      >
                        <option value="004001">전체금액</option>
                        <option value="004002">판매총액(%)</option>
                        <option value="004003">1인당 고정금액</option>
                        <option value="004004">판매당 고정금액</option>
                      </select>
                    </div>
                  </FieldRow>
                  <FieldRow label="신청금">
                    <div className="px-3 py-2">
                      {(() => {
                        const depositType = detailState.detail?.신청금구분 ?? '004001';
                        const depositValue = Number(detailState.detail?.신청금 ?? 100);
                        const isFullAmount = depositType === '004001'; // 전체금액
                        const isPercentage = depositType === '004002'; // 판매총액(%)
                        
                        // 표시값: 쉼표 포맷
                        const displayValue = depositValue.toLocaleString('ko-KR');
                        
                        return isFullAmount ? (
                          // 전체금액: 100으로 고정, 수정 불가
                          <div className="w-full px-3 py-2 bg-gray-100 text-gray-700 rounded border border-gray-300 cursor-not-allowed">
                            100%
                          </div>
                        ) : (
                          <div className="flex items-center gap-2">
                            <input
                              type="text"
                              value={displayValue}
                              onChange={(e) => {
                                // 쉼표 제거하고 숫자만 추출
                                const rawValue = e.target.value.replace(/,/g, '');
                                const numValue = rawValue === '' ? 0 : Number(rawValue);
                                
                                // 숫자가 아니면 무시
                                if (isNaN(numValue)) return;
                                
                                // 판매총액(%)는 100 이상 입력 불가
                                if (isPercentage && numValue > 100) {
                                  handleDetailChange('신청금', 100);
                                  return;
                                }
                                
                                handleDetailChange('신청금', numValue);
                              }}
                              onKeyDown={(e) => {
                                // 숫자, 백스페이스, 삭제, 화살표, 탭만 허용
                                if (
                                  !/[0-9]/.test(e.key) &&
                                  !['Backspace', 'Delete', 'ArrowLeft', 'ArrowRight', 'Tab'].includes(e.key)
                                ) {
                                  e.preventDefault();
                                }
                              }}
                              className="flex-1 px-3 py-2 border rounded"
                            />
                            <span className="text-sm text-gray-600 min-w-[60px]">
                              {depositValue <= 100 ? '%' : '원'}
                            </span>
                          </div>
                        );
                      })()}
                    </div>
                  </FieldRow>
                  <FieldRow label="신청금 기준일">
                    <div className="px-3 py-2">
                      <select
                        value={detailState.detail?.신청금기준일 ?? '203001'}
                        onChange={(e) => handleDetailChange('신청금기준일', e.target.value)}
                        className="w-full px-3 py-2 border rounded"
                      >
                        <option value="203001">투숙일</option>
                        <option value="203002">예약일</option>
                      </select>
                    </div>
                  </FieldRow>
                  <FieldRow label="신청금 시한">
                    <div className="px-3 py-2 flex items-center gap-2">
                      <input
                        type="number"
                        min="0"
                        value={detailState.detail?.신청금시한 ?? 0}
                        onChange={(e) => handleDetailChange('신청금시한', Number(e.target.value))}
                        className="w-24 px-3 py-2 border rounded"
                      />
                      <span className="text-sm text-gray-500">일</span>
                    </div>
                  </FieldRow>
                </div>
              </div>
              </>
            )}
          </div>
          )}
 
      {/* Footer */}
      <div className="sticky bottom-0 mt-6 pt-4 bg-white flex justify-end space-x-3">
        <button 
          onClick={requestClose} 
          className="px-5 py-2 border border-gray-300 rounded-md hover:bg-gray-50 transition-colors font-medium"
        >
          취소
        </button>
        <button 
          onClick={handleSave} 
          className="px-5 py-2 bg-primary-600 text-white rounded-md hover:bg-blue-700 transition-colors font-medium"
        >
          저장
        </button>
      </div>
    </div>
    </div>
  );
};

export default HotelSettingsModal;
